import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Shared button primitive. Three variants:
 * - primary: filled `var(--accent)` pill with white text; hover darkens to
 *   `var(--accent-hover)`.
 * - secondary: transparent with accent border; hover fills with accent.
 * - ghost: unstyled pill that picks up base text colors (used inside toolbars).
 *
 * The `active` prop renders a "pressed" look: the primary pill stays filled
 * and shifts to the darker accent.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renders the button in an active/pressed state (used by tab-like toggles). */
  active?: boolean;
  children?: ReactNode;
}

const BASE =
  "inline-flex items-center justify-center font-medium leading-tight text-center select-none " +
  "font-[var(--font-body)] border transition-colors cursor-pointer " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[color:var(--accent)] " +
  "disabled:opacity-60 disabled:cursor-not-allowed";

/** Per-variant base (non-active) styles. */
const VARIANT_BASE: Record<ButtonVariant, string> = {
  primary:
    'bg-[color:var(--accent)] text-[color:var(--text-light)] border-[color:var(--accent)] ' +
    'hover:bg-[color:var(--accent-hover)] hover:border-[color:var(--accent-hover)]',
  secondary:
    // Outline button; hover fills with accent.
    'bg-white text-[color:var(--accent)] border-[color:var(--accent)] ' +
    'hover:bg-[color:var(--accent)] hover:text-[color:var(--text-light)]',
  // Ghost: transparent pill with accent-light hover.
  ghost:
    'bg-transparent text-[color:var(--text-dark)] border-transparent ' +
    'hover:bg-[color:var(--accent-light)]',
};

/** `active`/pressed styles per variant — no invented ring. Just darker fill. */
const VARIANT_ACTIVE: Record<ButtonVariant, string> = {
  primary:
    'bg-[color:var(--accent-hover)] text-[color:var(--text-light)] border-[color:var(--accent-hover)]',
  secondary:
    'bg-[color:var(--accent)] text-[color:var(--text-light)] border-[color:var(--accent)]',
  ghost:
    'bg-[color:var(--accent-light)] text-[color:var(--accent)] border-transparent',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'px-2 py-1 text-xs rounded-full',
  md: 'px-3 py-1.5 text-[0.85rem] rounded-full',
  lg: 'px-4 py-2 text-base rounded-full',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', active, className, type, children, ...rest },
  ref,
) {
  const variantClass = active ? VARIANT_ACTIVE[variant] : VARIANT_BASE[variant];
  const classes = [BASE, variantClass, SIZES[size], className]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-pressed={active}
      className={classes}
      {...rest}
    >
      {children}
    </button>
  );
});

export default Button;
